import * as React from 'react';
import cls from 'classnames';

import { SectionBreak } from 'src/explore/elements/SectionBreak';
import { SvgUse } from 'src/shoppe/elements/SvgUse';
import { useRoutes } from 'src/explore/hooks/useRoutes';
import { useSettings } from 'src/explore/hooks/useSettings';
import { useShopConfig } from 'src/explore/hooks/useShopConfig';
import { isNativeMobileApp } from 'src/explore/services/mobile';

import { SocialButton } from './elements/SocialButton';

import styles from './index.module.sass';

interface Props {
  ssoOrigin?: string;
}

// On whitelabel subdomains, route OAuth through www.goldbelly.com so we don't
// need to whitelist every Prize subdomain in each provider's developer portal.
// Takes a route from routes.auth (e.g. "/auth/apple?origin=/checkout") and
// swaps the host to www, replacing the origin with the full Prize URL.
// On non-whitelabel subdomains, returns the route unchanged.
const buildAuthUrl = ( route: string, isWhitelabel?: boolean ): string => {
  if ( !isWhitelabel ) return route;

  const { hostname } = window.location;
  const wwwHost = [ 'www', ...hostname.split( '.' ).slice( 1 ) ].join( '.' );
  const url = new URL( route, window.location.origin );
  url.hostname = wwwHost;
  const relativePath = url.searchParams.get( 'origin' ) ?? window.location.pathname;
  url.searchParams.set( 'origin', window.location.origin + relativePath );
  return url.toString();
};

export const SSOButtons: React.FC<Props> = ({ ssoOrigin }) => {
  const routes = useRoutes();
  const settings = useSettings();
  const config = useShopConfig();

  if ( settings?.mobileApp?.showSso !== 't' && isNativeMobileApp()) {
    return null;
  }

  const googleHref = buildAuthUrl( routes.auth.google( ssoOrigin ), config?.isWhitelabel );
  const appleHref = buildAuthUrl( routes.auth.apple( ssoOrigin ), config?.isWhitelabel );
  const facebookHref = buildAuthUrl( routes.auth.facebook( ssoOrigin ), config?.isWhitelabel );

  return (
    <>
      <SectionBreak className={styles.sectionBreak} title="OR" />
      <div className={styles.authButtons}>
        {!isNativeMobileApp() && (
          <SocialButton
            className="spec__google-sso"
            href={googleHref}
            icon={<SvgUse ariaLabel="Google" className={cls( styles.icon )} name="social-google" />}
          >
            Continue with Google
          </SocialButton>
        )}
        <SocialButton
          className="spec__apple-sso"
          href={appleHref}
          icon={
            <SvgUse
              ariaLabel="Apple"
              className={cls( styles.apple, styles.icon )}
              name="social-apple"
            />
          }
        >
          Continue with Apple
        </SocialButton>
        {!isNativeMobileApp() && (
          <SocialButton
            className="spec__facebook-sso"
            href={facebookHref}
            icon={
              <SvgUse ariaLabel="Facebook" className={cls( styles.icon )} name="social-facebook" />
            }
          >
            Continue with Facebook
          </SocialButton>
        )}
      </div>
    </>
  );
};
